import React, { useEffect, useState, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { FaRegTrashAlt, FaRegEdit, FaDownload } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useKeycloak } from '@react-keycloak/web';

import CardImage from '../../../../Atoms/CardImage';
import Accordion from '../../../../Molecules/Accordion';
import TextArea from '../../../../Atoms/TextArea';
import Loader from '../../../../Atoms/Loader';
import useBrainstormStore from '../../../../../hooks/useBrainstormStore';
import { deleteFileFromS3, renderConceptPdf } from '../../../../../services/Brainstorm/BrainstormService';
import './styles.css';

import calendarImg from '../../../../../assets/img/project-covers/calendar.png';
import cardProjectImg from '../../../../../assets/img/project-covers/card_project.png';
import chatsImg from '../../../../../assets/img/project-covers/chats.png';
import dashboardImg from '../../../../../assets/img/project-covers/dashboard.png';
import flyImg from '../../../../../assets/img/project-covers/fly.png';
import ideaImg from '../../../../../assets/img/project-covers/idea.png';
import meetingImg from '../../../../../assets/img/project-covers/meeting.png';
import stikieNotesImg from '../../../../../assets/img/project-covers/stikieNotes.png';
import talkImg from '../../../../../assets/img/project-covers/talk.png';
import talk2Img from '../../../../../assets/img/project-covers/talk2.png';
import timerImg from '../../../../../assets/img/project-covers/timer.png';
import zoomMeetingImg from '../../../../../assets/img/project-covers/zoom_meeting.png';

const PROJECT_COVERS = [
    calendarImg,
    cardProjectImg,
    chatsImg,
    dashboardImg,
    flyImg,
    ideaImg,
    meetingImg,
    stikieNotesImg,
    talkImg,
    talk2Img,
    timerImg,
    zoomMeetingImg
];

const getImageForIdea = (ideaId) => {
    const index = ideaId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % PROJECT_COVERS.length;
    return PROJECT_COVERS[index];
};

export default function ConceptDetailsModal({ 
    show, 
    onHide,
    idea,
    brainstormId,
    onConceptDeleted,
    onConceptUpdated
}) {
    const { keycloak } = useKeycloak();
    const { getBrainstormById, getChallengeById, getIdeaById, deleteConcept, updateIdeaInGuidingQuestion } = useBrainstormStore();
    const justSavedRef = useRef(false);
    const [currentIdea, setCurrentIdea] = useState(null);
    const [guidingQuestion, setGuidingQuestion] = useState(null);
    const [challenge, setChallenge] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [editedConcept, setEditedConcept] = useState({
        como_funciona: '',
        impacto_negocio: '',
        valor_usuario: ''
    });

    useEffect(() => {
        if (!show || !idea || !brainstormId) {
            setCurrentIdea(null);
            setGuidingQuestion(null);
            setChallenge(null);
            setIsEditMode(false);
            return;
        }

        if (justSavedRef.current) {
            justSavedRef.current = false;
            return;
        }

        const latestIdea = getIdeaById(idea.id);
        if (latestIdea) {
            setCurrentIdea(latestIdea);
        } else {
            setCurrentIdea(idea);
        }

        const brainstorm = getBrainstormById(brainstormId);
        if (brainstorm) {
            for (const gq of brainstorm.guiding_questions || []) {
                const foundIdea = gq.ideas?.find(i => i.id === idea.id);
                if (foundIdea) {
                    setGuidingQuestion(gq);
                    if (gq.challenge_id) {
                        const challengeData = getChallengeById(brainstormId, gq.challenge_id);
                        setChallenge(challengeData);
                    }
                    break;
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, idea, brainstormId]);

    useEffect(() => {
        if (!isEditMode && currentIdea && currentIdea.concept) {
            setEditedConcept({
                como_funciona: currentIdea.concept.como_funciona || '',
                impacto_negocio: currentIdea.concept.impacto_negocio || '',
                valor_usuario: currentIdea.concept.valor_usuario || ''
            });
        }
    }, [currentIdea, isEditMode]);

    if (!currentIdea || !brainstormId) {
        return null;
    }

    const imageSrc = currentIdea.concept?.signed_image || getImageForIdea(currentIdea.id);
    const title = currentIdea.concept?.titulo_do_conceito || currentIdea.title;
    const description = currentIdea.concept?.descricao_do_conceito || currentIdea.description;

    const handleDeleteConcept = async () => {
        if (!currentIdea || !brainstormId || isDeleting) return;

        const conceptTitle = title || "este conceito";

        try {
            const result = await Swal.fire({
                title: 'Confirmar exclusão',
                text: `Tem certeza que deseja excluir o conceito "${conceptTitle}"?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ff6b6b',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Sim, excluir!',
                cancelButtonText: 'Cancelar',
                background: '#27272A',
                color: '#fff',
            });

            if (result.isConfirmed) {
                setIsDeleting(true);
                
                try {
                    const imageUrl = currentIdea.concept?.image;
                    if (imageUrl) {
                        try {
                            await deleteFileFromS3(imageUrl, keycloak.token);
                        } catch (err) {
                            console.error('Failed to delete S3 image:', err);
                        }
                    }

                    deleteConcept(brainstormId, currentIdea.id);

                    if (onConceptDeleted) {
                        onConceptDeleted(currentIdea.id);
                    }

                    Swal.fire({
                        toast: true,
                        title: 'Conceito excluído!',
                        text: 'O conceito foi excluído com sucesso.',
                        timer: 1500,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                        position: 'top-end',
                    });

                    onHide();
                } catch (error) {
                    console.error('Error deleting concept:', error);
                    Swal.fire({
                        toast: true,
                        title: 'Erro ao excluir conceito',
                        text: 'Ocorreu um erro ao excluir o conceito. Tente novamente.',
                        timer: 2000,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                        position: 'top-end',
                    });
                } finally {
                    setIsDeleting(false);
                }
            }
        } catch (error) {
            console.error('Error in delete confirmation:', error);
            setIsDeleting(false);
        }
    };

    const sanitizeFilename = (name) => {
        return name.replace(/[/\\:*?"<>|]/g, '_').trim() || 'conceito';
    };

    const handleDownloadPdf = async () => {
        if (isGeneratingPdf) return;
        setIsGeneratingPdf(true);

        try {
            const pdfBuffer = await renderConceptPdf(
                {
                    concept: currentIdea.concept,
                    challenge: challenge?.text || null,
                    guidingQuestion: guidingQuestion?.text || null,
                    fileName: sanitizeFilename(title),
                },
                keycloak.token
            );

            const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${sanitizeFilename(title)}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating PDF:', error);
            Swal.fire({
                toast: true,
                title: 'Erro ao gerar PDF',
                text: 'Não foi possível gerar o PDF. Tente novamente.',
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const handleEditClick = () => {
        if (currentIdea && currentIdea.concept) {
            setEditedConcept({
                como_funciona: currentIdea.concept.como_funciona || '',
                impacto_negocio: currentIdea.concept.impacto_negocio || '',
                valor_usuario: currentIdea.concept.valor_usuario || ''
            });
            setIsEditMode(true);
        } else {
            Swal.fire({
                toast: true,
                title: 'Conceito não disponível',
                text: 'Não foi possível entrar no modo de edição.',
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        }
    };

    const handleCancelEdit = () => {
        if (currentIdea && currentIdea.concept) {
            setEditedConcept({
                como_funciona: currentIdea.concept.como_funciona || '',
                impacto_negocio: currentIdea.concept.impacto_negocio || '',
                valor_usuario: currentIdea.concept.valor_usuario || ''
            });
        }
        setIsEditMode(false);
    };

    const handleSaveEdit = () => {
        if (!hasChanges()) {
            setIsEditMode(false);
            return;
        }

        if (!currentIdea || !brainstormId || !guidingQuestion) {
            Swal.fire({
                toast: true,
                title: 'Erro ao salvar',
                text: 'Dados do conceito não encontrados. Feche o modal e tente novamente.',
                timer: 2500,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
            return;
        }

        try {
            const brainstorm = getBrainstormById(brainstormId);
            if (!brainstorm) {
                throw new Error('Brainstorm not found');
            }

            const updatedConcept = {
                ...currentIdea.concept,
                como_funciona: editedConcept.como_funciona,
                impacto_negocio: editedConcept.impacto_negocio,
                valor_usuario: editedConcept.valor_usuario
            };

            updateIdeaInGuidingQuestion(
                brainstormId,
                guidingQuestion.id,
                currentIdea.id,
                { concept: updatedConcept }
            );

            const updatedIdea = getIdeaById(currentIdea.id);
            if (updatedIdea) {
                setCurrentIdea(updatedIdea);
            }

            justSavedRef.current = true;

            if (onConceptUpdated) {
                onConceptUpdated(currentIdea.id, updatedConcept);
            }

            Swal.fire({
                toast: true,
                title: 'Conceito atualizado!',
                text: 'O conceito foi atualizado com sucesso.',
                timer: 1500,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });

            setIsEditMode(false);
        } catch (error) {
            console.error('Error updating concept:', error);
            Swal.fire({
                toast: true,
                title: 'Erro ao atualizar conceito',
                text: 'Ocorreu um erro ao atualizar o conceito. Tente novamente.',
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        }
    };

    const hasChanges = () => {
        if (!currentIdea || !currentIdea.concept) return false;
        return (
            editedConcept.como_funciona !== (currentIdea.concept.como_funciona || '') ||
            editedConcept.impacto_negocio !== (currentIdea.concept.impacto_negocio || '') ||
            editedConcept.valor_usuario !== (currentIdea.concept.valor_usuario || '')
        );
    };

    return (
        <Modal 
            show={show} 
            onHide={isDeleting ? undefined : onHide}
            centered
            backdrop="static"
            className="concept-details-modal"
            size="xl"
        >
            {isDeleting && (
                <div className="concept-details-modal-loader-overlay">
                    <Loader size={40} />
                </div>
            )}
            <Modal.Header className="concept-details-modal-header">
                <div className="concept-details-modal-actions">
                    {!isEditMode ? (
                        <>
                            <button
                                className="concept-details-modal-action-btn"
                                title="Baixar PDF"
                                onClick={handleDownloadPdf}
                                disabled={isDeleting || isGeneratingPdf}
                                aria-label="Baixar PDF do conceito"
                            >
                                <FaDownload size={16} />
                            </button>
                            <button
                                className="concept-details-modal-action-btn"
                                title="Editar"
                                onClick={handleEditClick}
                                disabled={isDeleting}
                                aria-label="Editar conceito"
                            >
                                <FaRegEdit size={16} />
                            </button>
                            <button
                                className="concept-details-modal-action-btn"
                                title="Excluir"
                                onClick={handleDeleteConcept}
                                disabled={isDeleting}
                                aria-label="Excluir conceito"
                            >
                                <FaRegTrashAlt size={16} />
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                className="concept-details-modal-action-btn"
                                title="Salvar"
                                onClick={handleSaveEdit}
                                disabled={isDeleting}
                                aria-label="Salvar alterações"
                            >
                                ✓
                            </button>
                            <button 
                                className="concept-details-modal-action-btn"
                                title="Cancelar"
                                onClick={handleCancelEdit}
                                disabled={isDeleting}
                                aria-label="Cancelar edição"
                            >
                                ×
                            </button>
                        </>
                    )}
                </div>
                <button 
                    className="concept-details-modal-close"
                    onClick={onHide}
                    disabled={isDeleting}
                    aria-label="Fechar"
                >
                    ×
                </button>
            </Modal.Header>
            
            <Modal.Body className="concept-details-modal-body">
                <div className="concept-details-content">
                    {/* Left Section - Image */}
                    <div className="concept-details-image-section">
                        <div className="concept-details-image-card">
                            <CardImage 
                                src={imageSrc} 
                                alt={title} 
                            />
                        </div>
                    </div>

                    {/* Right Section - Content */}
                    <div className="concept-details-text-section">
                        <h2 className="concept-details-title">{title}</h2>
                        <p className="concept-details-description">{description}</p>
                        
                        <div className="concept-details-links">
                            <Accordion title="Desafio" type="without-border">
                        <p className="concept-details-accordion-content">
                            {challenge?.text || "Nenhum desafio associado."}
                        </p>
                    </Accordion>

                    <Accordion title="Pergunta norteadora" type="without-border">
                        <p className="concept-details-accordion-content">
                            {guidingQuestion?.text || "Nenhuma pergunta norteadora associada."}
                        </p>
                    </Accordion>
                        </div>
                    </div>
                </div>

                <div className="concept-details-accordions">
                    <Accordion title="Como funciona" type="border-bottom" forceOpen={isEditMode}>
                        {isEditMode ? (
                            <TextArea
                                value={editedConcept.como_funciona}
                                onChange={(e) => setEditedConcept({ ...editedConcept, como_funciona: e.target.value })}
                                placeholder="Como funciona este conceito?"
                                textAreaStyle={{ minHeight: '100px' }}
                                disabled={isDeleting}
                            />
                        ) : (
                            <p className="concept-details-accordion-content">
                                {currentIdea.concept?.como_funciona || "Como funciona este conceito?"}
                            </p>
                        )}
                    </Accordion>

                    <Accordion title="Impacto para o negócio" type="border-bottom" forceOpen={isEditMode}>
                        {isEditMode ? (
                            <TextArea
                                value={editedConcept.impacto_negocio}
                                onChange={(e) => setEditedConcept({ ...editedConcept, impacto_negocio: e.target.value })}
                                placeholder="Impacto para o negócio"
                                textAreaStyle={{ minHeight: '100px' }}
                                disabled={isDeleting}
                            />
                        ) : (
                            <p className="concept-details-accordion-content">
                                {currentIdea.concept?.impacto_negocio || "Impacto para o negócio"}
                            </p>
                        )}
                    </Accordion>

                    <Accordion title="Valor para o usuário" type="border-bottom" forceOpen={isEditMode}>
                        {isEditMode ? (
                            <TextArea
                                value={editedConcept.valor_usuario}
                                onChange={(e) => setEditedConcept({ ...editedConcept, valor_usuario: e.target.value })}
                                placeholder="Valor para o usuário"
                                textAreaStyle={{ minHeight: '100px' }}
                                disabled={isDeleting}
                            />
                        ) : (
                            <p className="concept-details-accordion-content">
                                {currentIdea.concept?.valor_usuario || "Valor para o usuário"}
                            </p>
                        )}
                    </Accordion>
                </div>
            </Modal.Body>
        </Modal>
    );
}
